/**
 * Bid-token display formatter shared between the FR-025 "Jeff's bid" panel
 * on `OvercallingDataEditingPage.tsx` and the FR-019a (d) auction-display
 * normalization in `OvercallingAuctionDisplay.tsx`.
 *
 * Tournament-hand auctions stored in `cardsgamehands.biddingsequence` use raw
 * tokens `P` / `X` / `XX` for Pass / Double / Redouble; this helper normalizes
 * them to the human-readable forms `Pass` / `Dbl` / `Rdbl` already used by the
 * Overcalling Practice page's `ResultFeedback` component and by the
 * problem-side auction display (Appendix A.6, which encodes auctions as
 * full-word tokens in `json_data->'auction'`). Suit bids (e.g. `1S`, `2H`,
 * `1NT`) are already in display form and pass through unchanged.
 *
 * Per FR-019a (d) (Session 2026-05-21), this normalization is applied to
 * EVERY non-masked bid in the auction display, not only to Jeff's bid in the
 * answer panel — the `?` mask at `jeff_seq` from FR-019a (a) is the ONLY
 * visual difference between the auction-sequence rendering and the FR-025
 * panel value, so divergent notation between them would be confusing during
 * curation.
 *
 * Defensive cases:
 * - Null / undefined → `'—'` (em-dash placeholder; matches the prior
 *   `formatJeffBid` behaviour so removing the in-page copy is a no-op for
 *   that call site).
 * - Already-display forms (`Pass`, `Dbl`, `Double`, `Rdbl`, `Redouble`) →
 *   normalised to the canonical short form (`Pass`, `Dbl`, `Rdbl`) so the
 *   helper is idempotent on its own output.
 * - Anything else (suit bids, `NT` calls, unknown strings) → returned as the
 *   trimmed uppercase form, matching the legacy `formatJeffBid` contract.
 *
 * The underlying data is NOT modified by this helper; the transformation is
 * display-only.
 */
export function formatBidToken(bid: string | null | undefined): string {
  if (!bid) return '—';
  const upper = bid.trim().toUpperCase();
  if (upper === '') return '—';
  if (upper === 'P' || upper === 'PASS') return 'Pass';
  if (upper === 'X' || upper === 'DBL' || upper === 'DOUBLE') return 'Dbl';
  if (upper === 'XX' || upper === 'RDBL' || upper === 'REDOUBLE') return 'Rdbl';
  return upper;
}
